import type { ApiResponse, PaginatedResponse, User } from '@/types/api';

import { api } from './api';

export const userService = {
  list: async () => {
    const response = await api.get<ApiResponse<User[]>>('/users');
    return response.data;
  },

  get: async (id: number) => {
    const response = await api.get<ApiResponse<User>>(`/users/${id}`);
    return response.data.data;
  },

  update: async (id: number, data: Partial<User>) => {
    const response = await api.put<ApiResponse<User>>(`/users/${id}`, data);
    return response.data.data;
  },

  updatePassword: async (id: number, password: string) => {
    const response = await api.put<ApiResponse<User>>(`/users/${id}/password`, { password });
    return response.data.data;
  },

  updateRole: async (id: number, role: User['role']) => {
    const response = await api.put<ApiResponse<User>>(`/users/${id}/role`, { role });
    return response.data.data;
  },

  updateAccess: async (id: number, accessEnabled: boolean) => {
    const response = await api.put<ApiResponse<User>>(`/users/${id}/access`, { accessEnabled });
    return response.data.data;
  },

  enableAccess: async (id: number) => {
    const response = await api.post<ApiResponse<User>>(`/users/${id}/enable-access`);
    return response.data.data;
  },

  listAll: async (params?: Record<string, unknown>) => {
    const response = await api.get<ApiResponse<Array<{ id: number; name: string; email: string }>>>('/users/all', { params });
    return response.data.data;
  },

  verifyEmail: async (id: number) => {
    const response = await api.post<ApiResponse<User>>(`/users/${id}/verify-email`);
    return response.data.data;
  },

  resendEmailVerification: async (id: number) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/users/${id}/resend-email`);
    return response.data.data;
  },

  resendWhatsAppVerification: async (id: number) => {
    const response = await api.post<ApiResponse<{ message: string }>>(`/users/${id}/resend-whatsapp`);
    return response.data.data;
  },

  uploadLogo: async (file: File) => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await api.post<ApiResponse<{ logoUrl: string }>>('/users/logo/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  getLogo: async () => {
    const response = await api.get<ApiResponse<{ logoUrl: string | null }>>('/users/logo');
    return response.data.data;
  },

  removeLogo: async () => {
    const response = await api.delete<ApiResponse<{ logoUrl: null }>>('/users/logo');
    return response.data.data;
  },

  getShowNotifications: async () => {
    const response = await api.get<ApiResponse<{ showNotifications: boolean }>>('/users/notifications-preference');
    return response.data.data;
  },

  setShowNotifications: async (showNotifications: boolean) => {
    const response = await api.put<ApiResponse<{ showNotifications: boolean }>>('/users/notifications-preference', { showNotifications });
    return response.data.data;
  },

  getDarkMode: async () => {
    const response = await api.get<ApiResponse<{ darkMode: boolean }>>('/users/dark-mode');
    return response.data.data;
  },

  setDarkMode: async (darkMode: boolean) => {
    const response = await api.put<ApiResponse<{ darkMode: boolean }>>('/users/dark-mode', { darkMode });
    return response.data.data;
  },
  permanentDelete: async (id: number, password: string) => {
    const response = await api.delete(`/users/${id}/permanent`, { data: { password } });
    return response.data;
  },
};

